import { useInjection } from 'inversify-react';
import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { AnalysisCard } from '../components/digiinsulin/AnalysisCard';
import { parseResponseData } from '../components/digiinsulin/utils';
import { API } from '../services/HTTP';

interface AnalysisFlag {
    passed: boolean;
    message?: string;
}

interface AnalysisStatusPayload {
    ok: boolean;
    flags?: Record<string, AnalysisFlag>;
}

interface UncertaintyDetail {
    std?: number | null;
    mean?: number | null;
    median?: number | null;
    ci95_low?: number | null;
    ci95_high?: number | null;
    cv_percent?: number | null;
    n_valid?: number | null;
}

interface DigiInsulinPredictionSummary {
    predicted_concentration?: number | null;
    uncertainty?: UncertaintyDetail | number | null;
}

interface DigiInsulinAnalysisListItem {
    id: string;
    imageS3Key: string;
    status: string;
    created_at: string;
    resultJson?: string;
}

interface DigiInsulinResultSummary {
    num_peaks?: number;
    prediction?: DigiInsulinPredictionSummary | null;
    analysisStatus?: AnalysisStatusPayload | null;
}

function parseResultSummary(resultJson?: string): DigiInsulinResultSummary | null {
    if (!resultJson) {
        return null;
    }

    try {
        return JSON.parse(resultJson) as DigiInsulinResultSummary;
    } catch (error) {
        console.error('Failed to parse DigiInsulin result summary:', error);
        return null;
    }
}

export function DigiInsulinResultsPage() {
    const api: API = useInjection<API>('API');
    const [results, setResults] = useState<DigiInsulinAnalysisListItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const loadResults = useCallback(async () => {
        setIsLoading(true);

        try {
            const response = await api.get('digiinsulin/results');
            setResults(parseResponseData<DigiInsulinAnalysisListItem[]>(response.data));
            setErrorMessage(null);
        } catch (error) {
            console.error('Failed to load DigiInsulin results:', error);
            setResults([]);
            setErrorMessage('Failed to load results. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, [api]);

    const handleReanalyze = useCallback(async (analysisId: string) => {
        try {
            await api.post(`digiinsulin/reanalyze/${encodeURIComponent(analysisId)}`, JSON.stringify({}));
            await loadResults();
        } catch (error) {
            console.error('Failed to re-analyze:', error);
            setErrorMessage('Failed to restart analysis. Please try again.');
        }
    }, [api, loadResults]);

    useEffect(() => {
        loadResults().catch((error) => console.error(error));
    }, [loadResults]);

    return (
        <>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
                <h1 className="mb-0">Analysis Results</h1>
                <Button as={Link as any} to="/digiinsulin/capture">New Analysis</Button>
            </div>

            {isLoading ? (
                <Spinner animation="border" />
            ) : errorMessage ? (
                <Alert variant="danger" className="mb-0">{errorMessage}</Alert>
            ) : results.length === 0 ? (
                <p className="text-muted">
                    No analyses yet. <Link to="/digiinsulin/capture">Capture your first image.</Link>
                </p>
            ) : (
                results.map((result) => {
                    const parsedResult = parseResultSummary(result.resultJson);

                    return (
                        <AnalysisCard
                            key={result.id}
                            sk={result.id}
                            imageS3Key={result.imageS3Key}
                            status={result.status}
                            created_at={result.created_at}
                            numPeaks={parsedResult?.num_peaks}
                            predictedConcentration={parsedResult?.prediction?.predicted_concentration}
                            uncertainty={parsedResult?.prediction?.uncertainty}
                            analysisStatus={parsedResult?.analysisStatus}
                            onReanalyze={() => handleReanalyze(result.id)}
                        />
                    );
                })
            )}
        </>
    );
}
